import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NamedLink from '../NamedLink/NamedLink';
import plusSignImage from './images/plus-sign.png';
import minusSignImage from './images/minus-sign.png';
import * as accordionData from './accordionData.js';
import css from './Accordion.module.css';

const renderParagraph = (paragraph, idx) => {
  if (typeof paragraph === 'string') {
    return <p key={idx}>{paragraph}</p>;
  }
  return (
    <p key={idx}>
      {paragraph.map((part, partIdx) =>
        typeof part === 'string' ? (
          part
        ) : (
          <NamedLink key={partIdx} name={part.name} className={css.link}>
            {part.text}
          </NamedLink>
        )
      )}
    </p>
  );
};

const AccordionIT = props => {

  const { rootClassName, className, urlQueryParams, location } = props;

  const categoryMap = {
    information_technology: 'itData',
    recruitment_and_hr: 'hrData',
    accountancy_banking_and_finance: 'financeData',

    environment_and_agriculture: 'environmentAndAgricultureData',
    engineering_and_manufacturing: 'engineeringAndManufacturingData',
    marketing_advertising_and_pr: 'marketingAdvertisingAndPRData',
    business_consulting_and_management: 'businessConsultingAndManagementData',
    law: 'lawData',
    teacher_training_and_education: 'teacherTrainingAndEducationData',
    retail: 'retailData',
    sales: 'salesData',
    hospitality_and_events_management: 'hospitalityData',
    social_care: 'socialCareData',
    science_and_pharmaceuticals: 'sciencePharmaceuticalsData',
    creative_arts_and_design: 'creativeArtsDesignData',

    transport_and_logistics: 'transportAndLogisticsData',
    charity_and_voluntary_work: 'charityAndVoluntaryWorkData',
    law_enforcement_and_security: 'lawEnforcementAndSecurityData',
    public_services_and_administration: 'publicServicesAndAdministrationData',
    leisure_sport_and_tourism: 'leisureSportsAndTourismData',
    media_and_internet: 'mediaAndInternetData',
    energy_and_utilities: 'energyAndUtilitiesData',
  };

  const pathnameMap = {
    'it-manager-informationtechnology': 'itManagerData',
    'marketing-manager-marketingandcommunications': 'marketingManagerData',
    'compliance-manager-consultingandstrategy': 'complianceManagerData',
    'data-scientist-informationtechnology': 'dataScientistData',
    'facilities-manager-administrationandofficesupport': 'facilitiesManagerData',
    'customer-service-representative-callcenterandcustomerservice': 'customerServiceRepresentativeData',
    'accounting-clerk-accounting': 'accountingClerkData',
    'staff-accountant-accounting': 'staffAccountantData',
    'it-director-informationtechnology': 'itDirectorData',
    'senior-accountant-accounting': 'seniorAccountantData',
    'accounts-manager-accounting': 'accountsManagerData',
    'chief-financial-officer-bankingandfinancialservices': 'chiefFinancialOfficerData',
    'it-support-specialist-informationtechnology': 'itSupportSpecialistData',
    'public-relations-manager-marketingandcommunications': 'prManagerData',
    'registered-nurse-healthcareandmedical': 'registeredNurseData',
    'hr-manager-humanresourcesandrecruitment': 'hrManagerData',
    'sales-manager-sales': 'salesManagerData',
    'operations-manager-executive': 'operationsManagerData',
    'plant-manager-manufacturingtransportandlogistics': 'plantManagerData',
    'hr-assistant-humanresourcesandrecruitment': 'hrAssistantData',
    'paralegal-legal': 'paralegalManagerData',
    'engineering-manager-engineering': 'engineeringManagerData',
    'sales-representative-sales': 'salesRepresentativeData',
    'creative-director-advertisingartsandmedia': 'creativeDirectorData',
    'school-principal-educationandtraining': 'schoolPrincipalData',
    'office-manager-administrationandofficesupport': 'officeManagerData',
    'system-administrator-informationtechnology': 'systemsAdministratorData',
    'operations-assistant-administrationandofficesupport': 'operationsAssistantData',
    'production-worker-manufacturingtransportandlogistics': 'productionWorkerData',
    'data-analyst-informationtechnology': 'dataAnalystData',
    'junior-engineer-engineering': 'juniorEngineerData',
    'marketing-coordinator-marketingandcommunications': 'marketingCoordinatorData',

    'engineer-engineering': 'engineerData',
    'regional-sales-manager-sales': 'regionalSalesManagerData',
    'research-and-development-manager': 'rAndDManagerData',
    'medical-assistant-healthcareandmedical': 'medicalAssistantData',
    'research-assistant-administrationandofficesupport': 'researchAssistantData',

    'chief-information-officer': 'chiefInformationOfficerData',
    'senior-marketing-manager': 'seniorMarketingManagerData',
    'chief-sales-officer': 'chiefSalesOfficerData',
    'chief-human-resources-officer': 'chiefHumanResourcesOfficerData',
    'marketing-director-marketingandcommunications': 'marketingDirectorData',
    'chief-marketing-officer-marketingandcommunications': 'chiefMarketingOfficerData',
    'sales-director-sales': 'salesDirectorData',
    'hr-generalist-humanresourcesandrecruitment': 'hrGeneralistData',
    'hr-director-humanresourcesandrecruitment': 'hrDirectorData',
    'public-relations-director-marketingandcommunications': 'prDirectorData',
    'public-relations-marketingandcommunications': 'prSpecialistData',
    'compliance-analyst-consultingandstrategy': 'complianceAnalystData',

    'vp-of-engineering-informationtechnology': 'vicePresidentEngineeringCareerMentorData',
    'business-intelligence-analyst-consultingandstrategy': 'businessIntelligenceAnalystCareerMentorData',
    'chief-data-officer-informationtechnology': 'chiefDataOfficerCareerMentorData',

    'production-supervisor-manufacturingtransportandlogistics': 'productionSupervisorData',
    'operations-analyst-administrationandofficesupport': 'operationsAnalystData',
    'director-of-operations-executive': 'operationsDirectorData',
    'research-scientist-scienceandtechnology': 'researchScientistData',
    'maintenance-worker-tradesandservices': 'maintenanceWorkerData',
    'maintenance-supervisor-tradesandservices': 'maintenanceSupervisorData',
    'director-of-facilities-administrationandofficesupport': 'directorOfFacilitiesData',
    'nurse-practitioner-healthcareandmedical': 'nursePractitionerData',
    'director-of-nursing-healthcareandmedical': 'nursingDirectorData',
    'senior-graphic-designer-advertisingartsandmedia': 'seniorGraphicDesignerData',

    'retail-assistant-manager-retailandconsumerproducts': 'retailAssistantManagerData',
    'graduate-teaching-assistant-educationandtraining': 'teachingAssistantData',
    'junior-graphic-designer-advertisingartsandmedia': 'juniorGraphicDesignerData',
    'administrative-services-manager-administrationandofficesupport': 'administrativeServicesManagerData',
    'executive-assistant-administrationandofficesupport': 'executiveAssistantData',
    'office-coordinator-administrationandofficesupport': 'officeCoordinatorData',
    'administrative-assistant-administrationandofficesupport': 'administrativeAssistantData',
    'customer-service-manager-callcenterandcustomerservice': 'customerServiceManagerData',
    'lead-teacher-educationandtraining': 'leadTeacherData',
    'teacher-educationandtraining': 'teacherData',

    'retail-associate-retailandconsumerproducts': 'retailAssociateData',
    'retail-merchandiser-retailandconsumerproducts': 'retailMerchandiserData',
    'retail-manager-retailandconsumerproducts': 'retailManagerData',
    'retail-sales-consultant-retailandconsumerproducts': 'retailSalesConsultantData',
    'chartered-accountant-accountant': 'charteredAccountantData',
    'ux-designer-informationtechnology': 'uxDesignerData',
    'marketing-analyst-advertisingartsandmedia': 'marketingAnalystData',
    'corporate-lawyer-legal': 'corporateLawyerData',
    'administrative-analyst-consultingandstrategy': 'administrativeAnalystData',
    'social-media-manager-marketingandcommunications': 'socialMediaManagerData',

    'it-architect-informationtechnology': 'ITArchitectData',
    'it-auditor-informationtechnology': 'ITAuditorData',
    'it-business-analyst-informationtechnology': 'ITBusinessAnalystData',
    'it-consultant-informationtechnology': 'ITConsultantData',
    'it-engineer-informationtechnology': 'ITEngineerData',
    'it-project-manager-informationtechnology': 'ITProjectManagerData',
    'it-specialist-informationtechnology': 'ITSpecialistData',
    'it-supervisor-informationtechnology': 'ITSupervisorData',
    'it-support-analyst-informationtechnology': 'ITSupportAnalystData',
    'it-technician-informationtechnology': 'ITTechnicianData',

    'financial-advisor-bankingandfinancialservices': 'financialAdvisorData',
    'academic-advisor-educationandtraining': 'academicAdvisorData',
    'it-support-engineer-informationtechnology': 'ITSupportEngineerData',
    'hr-administrator-humanresourcesandrecruitment': 'hrAdministratorData',
    'hr-associate-humanresourcesandrecruitment': 'hrAssociateData',
    'hr-business-partner-humanresourcesandrecruitment': 'hrBusinessPartnerData',
    'finance-manager-bankingandfinancialservices': 'financeManagerData',
    'hr-intern-humanresourcesandrecruitment': 'hrInternCareerData',
    'financial-analyst-bankingandfinancialservices': 'financialAnalystData',
    'financial-planner-bankingandfinancialservices': 'financialPlannerData',

    'technical-trainer-educationandtraining': 'technicalTrainerData',
    'financial-consultant-bankingandfinancialservices': 'financialConsultantData',
    'finance-director-accounting': 'financeDirectorData',
    'financial-counselor-healthcareandmedical': 'financialCounsellorData',
    'financial-coordinator-accounting': 'financialCoordinatorData',
    'financial-accountant-accountant': 'financialAccountantData',
    'finance-assistant-bankingandfinancialservices': 'financeAssistantData',
    'teacher-assistant-educationandtraining': 'teacherAssistantData',
    'education-director-educationandtraining': 'educationDirectorData',
    'corporate-trainer-educationandtraining': 'corporateTrainerCareerData',

    'business-lawyer-legal': 'businessLawyerData',
    'patent-lawyer-legal': 'patentLawyerData',
    'real-estate-lawyer-legal': 'realEstateLawyerData',
    'criminal-lawyer-legal': 'criminalLawyerData',
    'family-lawyer-legal': 'familyLawyerData',
    'law-clerk-legal': 'lawClerkData',
    'lawyer-legal': 'lawyerData',
    'business-administrator-executive': 'businessAdministratorData',
    'business-advisor-consultingandstrategy': 'businessAdvisorData',
    'business-analyst-informationtechnology': 'businessAnalystData',

    'business-consultant-consultingandstrategy': 'businessConsultantData',
    'business-development-consultant-consultingandstrategy': 'businessDevelopmentConsultantData',
    'business-development-analyst-consultingandstrategy': 'businessDevelopmentAnalystData',
    'business-development-associate-sales': 'businessDevelopmentAssociateData',
    'business-development-director-sales': 'businessDevelopmentDirectorData',
    'business-development-executive-sales': 'businessDevelopmentExecutiveData',
    'business-development-manager-sales': 'businessDevelopmentManagerData',
    'business-development-officer-sales': 'businessDevelopmentOfficerData',
    'business-development-specialist-consultingandstrategy': 'businessDevelopmentSpecialistData',
    'business-intelligence-bi-developer-consultingandstrategy': 'businessIntelligenceDeveloperData',

    'business-systems-analyst-informationtechnology': 'businessSystemsAnalystData',
    'technical-business-analyst-informationtechnology': 'technicalBusinessAnalystData',
    'senior-business-analyst-consultingandstrategy': 'seniorBusinessAnalystData',
    'insurance-consultant-insurance': 'insuranceConsultantData',
    'digital-marketing-consultant-marketingandcommunications': 'digitalMarketingConsultantData',
    'immigration-consultant-hospitalityandtourism': 'immigrationConsultantData',
    'independent-consultant-consultingandstrategy': 'independentConsultantData',
    'image-consultant-consultingandstrategy': 'imageConsultantData',
    'business-manager-administrationandofficesupport': 'businessManagerData',
    'business-operations-manager-executive': 'businessOperationsManagerData',

    'leasing-consultant-realestateandproperty': 'leasingConsultantData',
    'management-consultant-executive': 'managementConsultantData',
    'marketing-consultant-advertisingartsandmedia': 'marketingConsultantData',
    'property-consultant-realestateandproperty': 'propertyConsultantData',
    'recruitment-consultant-humanresourcesandrecruitment': 'recruitmentConsultantData',
    'sales-consultant-sales': 'salesConsultantData',
    'senior-consultant-informationtechnology': 'seniorConsultantData',
    'solar-sales-consultant-sales': 'solarSalesConsultantData',
    'technical-consultant-informationtechnology': 'technicalConsultantData',
    'travel-consultant-consultingandstrategy': 'travelConsultantData',

    'energy-consultant-publicservices': 'energyConsultantData',
    'content-marketing-manager-marketingandcommunications': 'contentMarketingManagerData',
    'digital-marketing-assistant-marketingandcommunications': 'digitalMarketingAssistantData',
    'digital-marketing-coordinator-marketingandcommunications': 'digitalMarketingCoordinatorData',
    'digital-marketing-manager-marketingandcommunications': 'digitalMarketingManagerData',
    'marketing-assistant-marketingandcommunications': 'marketingAssistantData',
    'marketing-associate-advertisingartsandmedia': 'marketingAssociateData',
    'email-marketing-specialist-marketingandcommunications': 'emailMarketingSpecialistData',
    'marketing-executive-marketingandcommunications': 'marketingExecutiveData',
    'wedding-consultant-consultingandstrategy': 'weddingConsultantData',

    'marketing-officer-marketingandcommunications': 'marketingOfficerData',
    'marketing-intern-marketingandcommunications': 'marketingInternData',
    'online-marketing-executive-marketingandcommunications': 'onlineMarketingExecutiveData',
    'online-marketing-manager-marketingandcommunications': 'onlineMarketingManagerData',
    'sales-and-marketing-executive-sales': 'salesAndMarketingExecutiveData',
    'sales-and-marketing-associate-sales': 'salesAndMarketingAssociateData',
    'sales-and-marketing-manager-marketingandcommunications': 'salesAndMarketingManagerData',
    'product-marketing-manager-marketingandcommunications': 'productMarketingManagerData',
    'senior-product-marketing-manager-consultingandstrategy': 'seniorProductMarketingManagerData',
    'marketing-specialist-marketingandcommunications': 'marketingSpecialistData',

    'sales-development-representative-sales': 'salesDevelopmentRepresentativeData',
    'market-research-analyst-consultingandstrategy': 'marketResearchAnalystData',
    'sports-marketing-manager-marketingandcommunications': 'sportsMarketingManagerData',
    'telemarketing-executive-callcenterandcustomerservice': 'telemarketingExecutiveData',
    'telemarketer-callcenterandcustomerservice': 'telemarketerData',
    'recruitment-marketer-marketingandcommunications': 'recruitmentMarketerData',
    'online-marketer-marketingandcommunications': 'onlineMarketerData',
    'marketer-marketingandcommunications': 'marketerData',
    'vp-of-marketing-marketingandcommunications': 'vpOfMarketingData',
    'marketing-management-marketingandcommunications': 'marketingManagementData',

    'application-support-engineer-informationtechnology': 'applicationSupportEngineerData',
    'architectural-engineer-engineering': 'architecturalEngineerData',
    'aerospace-engineer-engineering': 'aerospaceEngineerData',
    'public-speaker-educationandtraining': 'publicSpeakerData',
    'brand-strategist-marketingandcommunications': 'brandStrategistData',
    'brand-manager-marketingandcommunications': 'brandManagerData',
    'brand-ambassador-marketingandcommunications': 'brandAmbassadorData',
    'publicist-marketingandcommunications': 'publicistData',
    'advertising-manager-advertisingartsandmedia': 'advertisingManagerData',
    'advertising-sales-representative-sales': 'advertisingSalesRepresentativeData',

    'computer-hardware-engineer-informationtechnology': 'computerHardwareEngineerInformationtechnologyData',
    'computer-engineer-informationtechnology': 'computerEngineerInformationtechnologyData',
    'civil-engineer-civilandstructuralengineering': 'civilEngineerCivilandstructuralengineeringData',
    'chief-engineer-engineering': 'chiefEngineerEngineeringData',
    'chemical-engineer-engineering': 'chemicalEngineerEngineeringData',
    'biomedical-engineer-engineering': 'biomedicalEngineerEngineeringData',
    'biochemical-engineer-engineering': 'biochemicalEngineerEngineeringData',
    'big-data-engineer-informationtechnology': 'bigDataEngineerInformationtechnologyData',
    'assistant-engineer-educationandtraining': 'assistantEngineerEducationandtrainingData',
    'audio-engineer-tradesandservices': 'audioEngineerTradesandservicesData',

    'embedded-software-engineer-informationtechnology': 'embeddedSoftwareEngineerInformationtechnologyData',
    'electronics-engineer-engineering': 'electronicsEngineerEngineeringData',
    'electrical-engineer-engineering': 'electricalEngineerEngineeringData',
    'director-of-engineering-executive': 'directorOfEngineeringExecutiveData',
    'devops-engineer-informationtechnology': 'devopsEngineerInformationtechnologyData',
    'desktop-support-engineer-informationtechnology': 'desktopSupportEngineerInformationtechnologyData',
    'design-engineer-engineering': 'designEngineerEngineeringData',
    'data-engineer-informationtechnology': 'dataEngineerInformationtechnologyData',
    'cyber-security-engineer-informationtechnology': 'cyberSecurityEngineerInformationtechnologyData',
    'controls-engineer-manufacturingtransportandlogistics': 'controlsEngineerManufacturingtransportandlogisticsData',

    'manufacturing-engineer-engineering': 'manufacturingEngineerEngineeringData',
    'maintenance-engineer-engineering': 'maintenanceEngineerEngineeringData',
    'locomotive-engineer-manufacturingtransportandlogistics': 'locomotiveEngineerManufacturingtransportandlogisticsData',
    'java-software-engineer-informationtechnology': 'javaSoftwareEngineerInformationtechnologyData',
    'infrastructure-engineer-informationtechnology': 'infrastructureEngineerInformationtechnologyData',
    'industrial-engineer-engineering': 'industrialEngineerEngineeringData',
    'geotechnical-engineer-engineering': 'geotechnicalEngineerEngineeringData',
    'full-stack-engineer-informationtechnology': 'fullStackEngineerInformationtechnologyData',
    'field-engineer-engineering': 'fieldEngineerEngineeringData',
    'environmental-engineer-engineering': 'environmentalEngineerEngineeringData',
  };

  const categoryKey = categoryMap[urlQueryParams?.pub_category];
  const pathnameKey = Object.keys(pathnameMap).find(key => {
    const pattern = new RegExp(`(^|/)${key}(/|$)`);
    return pattern.test(location?.pathname);
  });
  const dataKey = categoryKey || pathnameMap[pathnameKey];
  const data = dataKey ? accordionData[dataKey] : null;

  if (!data) return null;

  const [accordionIndex, setAccordionIndex] = useState(0);

  const toggleAccordion = index => {
    setAccordionIndex(index === accordionIndex ? -1 : index);
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <h2 className={css.title}>{data?.mainTitle}</h2>
      <div className={css.accordion}>
        {data?.accordionData?.map((item, i) => (
          <div
            key={i}
            className={classNames(css.accordionItem, {
              [css.accordionItemActive]: accordionIndex === i,
            })}
          >
            <h3 className={css.accordionTitle} onClick={() => toggleAccordion(i)}>
              {item.title}
              <img src={accordionIndex === i ? minusSignImage : plusSignImage} alt="" />
            </h3>
            {accordionIndex === i && (
              <div className={css.accordionDescription}>
                {item.description.map(renderParagraph)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

AccordionIT.defaultProps = {
  rootClassName: null,
  className: null,
};

AccordionIT.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  urlQueryParams: PropTypes.object,
  location: PropTypes.object,
};

export default AccordionIT;